import React from "react";
import { EntityAuditFooter } from "@/components/shared/EntityAuditFooter";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit, Layers, Tag, Box, Scale } from "lucide-react";
import EntityContextPanel from "@/components/shared/EntityContextPanel";

interface MaterialTypeDetailDrawerProps {
  item: any | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (item: any) => void;
}

export function MaterialTypeDetailDrawer({
  item,
  open,
  onOpenChange,
  onEdit,
}: MaterialTypeDetailDrawerProps) {
  if (!item) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-6 flex flex-col justify-between z-[10000] overflow-y-auto">
        <div className="space-y-6">
          <SheetHeader className="p-0 space-y-1">
            <div className="flex items-center gap-2">
              <span className="p-2 bg-emerald-50 text-emerald-700 rounded-lg">
                <Box className="w-5 h-5" />
              </span>
              <div>
                <SheetTitle className="text-lg font-bold text-slate-900 font-mono">
                  {item.code}
                </SheetTitle>
                <SheetDescription className="text-xs text-slate-500">
                  Cấu hình định dạng vật tư ngành in
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          {/* Details Card */}
          <div className="space-y-4 text-xs">
            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between pb-2 border-b border-slate-200/60">
                <span className="text-slate-500 flex items-center gap-1.5 font-medium">
                  <Tag className="w-3.5 h-3.5 text-slate-400" /> Tên / Nhãn hiển thị
                </span>
                <span className="font-bold text-slate-900 text-sm">
                  {item.label || item.code}
                </span>
              </div>

              <div className="flex items-center justify-between pb-2 border-b border-slate-200/60">
                <span className="text-slate-500 flex items-center gap-1.5 font-medium">
                  <Layers className="w-3.5 h-3.5 text-slate-400" /> Nhóm vật tư
                </span>
                <Badge variant="outline" className="bg-white border-slate-200 text-slate-800 font-semibold">
                  {item.group || "Chưa phân nhóm"}
                </Badge>
              </div>

              <div className="flex items-center justify-between pb-2 border-b border-slate-200/60">
                <span className="text-slate-500 flex items-center gap-1.5 font-medium">
                  <Box className="w-3.5 h-3.5 text-slate-400" /> Chất liệu nền
                </span>
                <span className="font-semibold text-slate-800">
                  {item.material || "—"}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-slate-500 flex items-center gap-1.5 font-medium">
                  <Scale className="w-3.5 h-3.5 text-slate-400" /> Định lượng (GSM)
                </span>
                <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700 font-mono font-bold">
                  {item.gsm ? `${item.gsm} gsm` : "—"}
                </Badge>
              </div>
            </div>

            {/* Quick Helper Note */}
            <div className="p-3 bg-blue-50/60 border border-blue-200/80 rounded-lg text-blue-900 text-[11px] leading-relaxed">
              <strong>Ứng dụng:</strong> Định dạng vật tư quy định chuẩn giấy (Couche, Ivory, Duplex, Kraft...) và định lượng phục vụ tính toán BOM, định mức phôi và bóc tách dự toán kỹ thuật.
            </div>

            {/* Context Panel (Comments, Attachments, Audit Logs) */}
            <div className="pt-2 border-t border-slate-200">
              <EntityContextPanel entityType="material_type" entityId={item.id || item.code} />
              <EntityAuditFooter entityType="material_type" entityId={item.id || item.code} fallback={{ created: item.createdAt, updated: item.updatedAt }} className="mt-3 text-[11px] text-slate-500 flex items-center gap-x-3 gap-y-1 flex-wrap" />
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-200">
          <Button
            variant="outline"
            size="sm"
            className="h-9 px-4 text-xs font-medium text-slate-700"
            onClick={() => onOpenChange(false)}
          >
            Đóng
          </Button>
          <Button
            size="sm"
            className="h-9 px-4 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5 shadow-sm"
            onClick={() => {
              onOpenChange(false);
              onEdit?.(item);
            }}
          >
            <Edit className="w-3.5 h-3.5" />
            Chỉnh Sửa
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
